'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BandwidthCalculator() {
  const t = useTranslations("tools.bandwidthCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Converter state
  const [converterInputs, setConverterInputs] = useState({
    amount: 500,
    unit: 'MB'
  });
  const [converterResults, setConverterResults] = useState<any>(null);

  // Time calculator state
  const [timeInputs, setTimeInputs] = useState({
    fileSize: 500,
    fileSizeUnit: 'MB',
    bandwidth: 5,
    bandwidthUnit: 'mb'
  });
  const [timeResults, setTimeResults] = useState<any>(null);

  // Website calculator state
  const [websiteInputs, setWebsiteInputs] = useState({
    pageViews: 5000,
    pageViewsUnit: 'day',
    pageSize: 500,
    pageSizeUnit: 'KB',
    redundancy: 2
  });
  const [websiteResults, setWebsiteResults] = useState<any>(null);

  // Hosting converter state
  const [hostingInputs, setHostingInputs] = useState({
    monthlyUsage: '',
    monthlyUsageUnit: 'GB',
    hostingBandwidth: '',
    hostingBandwidthUnit: 'mb'
  });
  const [hostingResults, setHostingResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const unitConversions: { [key: string]: number } = {
    'b': 1,
    'kb': 1000,
    'mb': 1000000,
    'gb': 1000000000,
    'tb': 1000000000000,
    'B': 8,
    'KB': 8000,
    'MB': 8000000,
    'GB': 8000000000,
    'TB': 8000000000000
  };

  const convertToBits = (value: number, unit: string): number => {
    return value * unitConversions[unit];
  };

  const convertFromBits = (bits: number, unit: string): number => {
    return bits / unitConversions[unit];
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000000000) return (num / 1000000000000).toFixed(2) + ' trillion';
    if (num >= 1000000000) return (num / 1000000000).toFixed(2) + ' billion';
    if (num >= 1000000) return (num / 1000000).toFixed(2) + ' million';
    if (num >= 1000) return (num / 1000).toFixed(2) + ' thousand';
    return num.toFixed(6);
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return seconds.toFixed(2) + ' seconds';
    if (seconds < 3600) return (seconds / 60).toFixed(2) + ' minutes';
    if (seconds < 86400) return (seconds / 3600).toFixed(2) + ' hours';
    if (seconds < 2592000) return (seconds / 86400).toFixed(2) + ' days';
    if (seconds < 31536000) return (seconds / 2592000).toFixed(2) + ' months';
    return (seconds / 31536000).toFixed(2) + ' years';
  };

  const handleConverterCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (converterInputs.amount <= 0) {
      alert(t('errorPositiveNumber'));
      return;
    }

    const bits = convertToBits(converterInputs.amount, converterInputs.unit);

    const conversions = [
      { label: 'bits (b)', value: convertFromBits(bits, 'b') },
      { label: 'kilobits (kb)', value: convertFromBits(bits, 'kb') },
      { label: 'megabits (mb)', value: convertFromBits(bits, 'mb') },
      { label: 'gigabits (gb)', value: convertFromBits(bits, 'gb') },
      { label: 'terabits (tb)', value: convertFromBits(bits, 'tb') },
      { label: 'Bytes (B)', value: convertFromBits(bits, 'B') },
      { label: 'Kilobytes (KB)', value: convertFromBits(bits, 'KB') },
      { label: 'Megabytes (MB)', value: convertFromBits(bits, 'MB') },
      { label: 'Gigabytes (GB)', value: convertFromBits(bits, 'GB') },
      { label: 'Terabytes (TB)', value: convertFromBits(bits, 'TB') }
    ];

    setConverterResults(conversions);
  };

  const handleTimeCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (timeInputs.fileSize <= 0 || timeInputs.bandwidth <= 0) {
      alert(t('errorPositiveNumber'));
      return;
    }

    const fileSizeBits = convertToBits(timeInputs.fileSize, timeInputs.fileSizeUnit);
    const bandwidthBitsPerSecond = convertToBits(timeInputs.bandwidth, timeInputs.bandwidthUnit);

    const timeSeconds = fileSizeBits / bandwidthBitsPerSecond;

    setTimeResults({ time: formatTime(timeSeconds) });
  };

  const handleWebsiteCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (websiteInputs.pageViews <= 0 || websiteInputs.pageSize <= 0 || websiteInputs.redundancy <= 0) {
      alert(t('errorPositiveNumber'));
      return;
    }

    const timeMultipliers: { [key: string]: number } = {
      'second': 1,
      'minute': 60,
      'hour': 3600,
      'day': 86400,
      'week': 604800,
      'month': 2592000,
      'year': 31536000
    };

    const pageViewsPerSecond = websiteInputs.pageViews / timeMultipliers[websiteInputs.pageViewsUnit];
    const pageSizeBytes = convertToBits(websiteInputs.pageSize, websiteInputs.pageSizeUnit) / 8;

    const bandwidthBytesPerSecond = pageViewsPerSecond * pageSizeBytes * websiteInputs.redundancy;
    const bandwidthBitsPerSecond = bandwidthBytesPerSecond * 8;

    const monthlyBytes = bandwidthBytesPerSecond * 2592000;

    let bandwidthDisplay = '';
    if (bandwidthBitsPerSecond >= 1000000000) {
      bandwidthDisplay = (bandwidthBitsPerSecond / 1000000000).toFixed(2) + ' Gbit/s';
    } else if (bandwidthBitsPerSecond >= 1000000) {
      bandwidthDisplay = (bandwidthBitsPerSecond / 1000000).toFixed(2) + ' Mbit/s';
    } else if (bandwidthBitsPerSecond >= 1000) {
      bandwidthDisplay = (bandwidthBitsPerSecond / 1000).toFixed(2) + ' Kbit/s';
    } else {
      bandwidthDisplay = bandwidthBitsPerSecond.toFixed(2) + ' bit/s';
    }

    let monthlyDisplay = '';
    if (monthlyBytes >= 1000000000000) {
      monthlyDisplay = (monthlyBytes / 1000000000000).toFixed(2) + ' TB';
    } else if (monthlyBytes >= 1000000000) {
      monthlyDisplay = (monthlyBytes / 1000000000).toFixed(2) + ' GB';
    } else if (monthlyBytes >= 1000000) {
      monthlyDisplay = (monthlyBytes / 1000000).toFixed(2) + ' MB';
    } else if (monthlyBytes >= 1000) {
      monthlyDisplay = (monthlyBytes / 1000).toFixed(2) + ' KB';
    } else {
      monthlyDisplay = monthlyBytes.toFixed(2) + ' Bytes';
    }

    setWebsiteResults({
      bandwidth: bandwidthDisplay,
      monthly: monthlyDisplay
    });
  };

  const handleHostingCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!hostingInputs.monthlyUsage && !hostingInputs.hostingBandwidth) {
      alert(t('errorEnterOneValue'));
      return;
    }

    if (hostingInputs.monthlyUsage && hostingInputs.hostingBandwidth) {
      alert(t('errorEnterOnlyOne'));
      return;
    }

    const secondsInMonth = 2592000;

    if (hostingInputs.monthlyUsage) {
      const usageValue = parseFloat(hostingInputs.monthlyUsage);

      if (usageValue <= 0) {
        alert(t('errorPositiveNumber'));
        return;
      }

      const totalBits = convertToBits(usageValue, hostingInputs.monthlyUsageUnit);
      const bitsPerSecond = totalBits / secondsInMonth;

      const bandwidthValue = convertFromBits(bitsPerSecond, hostingInputs.hostingBandwidthUnit);

      const unitDisplay = hostingInputs.hostingBandwidthUnit
        .replace('b', 'bit/s')
        .replace('kb', 'Kbit/s')
        .replace('mb', 'Mbit/s')
        .replace('gb', 'Gbit/s')
        .replace('tb', 'Tbit/s');

      setHostingResults({
        label: t('equivalentBandwidth'),
        value: bandwidthValue.toFixed(4) + ' ' + unitDisplay
      });
    } else {
      const bandwidthValue = parseFloat(hostingInputs.hostingBandwidth);

      if (bandwidthValue <= 0) {
        alert(t('errorPositiveNumber'));
        return;
      }

      const bitsPerSecond = convertToBits(bandwidthValue, hostingInputs.hostingBandwidthUnit);
      const totalBits = bitsPerSecond * secondsInMonth;

      const usageValue = convertFromBits(totalBits, hostingInputs.monthlyUsageUnit);

      setHostingResults({
        label: t('monthlyUsage'),
        value: usageValue.toFixed(4) + ' ' + hostingInputs.monthlyUsageUnit
      });
    }
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#06b6d4',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    success: '#15803d',
    successBg: 'rgba(240, 253, 244, 0.9)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const cardHoverStyle = {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  };

  const h3Style = getResponsiveStyle(
    {
      color: colors.primary,
      marginBottom: '1.5rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputRowStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.75rem', alignItems: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      background: 'white',
      outline: 'none',
    },
    { width: '100%', flex: 'none' },
    { flex: 1, width: 'auto' },
    { flex: 1, width: 'auto' }
  );

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      background: 'white',
      outline: 'none',
    },
    { width: '100%', flex: 'none' },
    { flex: 1.2, width: 'auto' },
    { flex: 1.2, width: 'auto' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '0.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
    { padding: '1rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = {
    width: '100%',
    padding: '0.75rem 1.5rem',
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginTop: '0.5rem',
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      marginTop: '2rem',
      border: '2px solid #86efac',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      color: colors.success,
      marginBottom: '1.5rem',
      fontWeight: 700,
      textAlign: 'center' as const,
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem',
  };

  const resultItemStyle = {
    background: 'white',
    padding: '1rem 1.5rem',
    borderRadius: '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  };

  const resultLabelStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: '#475569',
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: colors.primary,
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const conversionTableStyle = {
    marginTop: '1.5rem',
    overflowX: 'auto' as const,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: 'white',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  };

  const thStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      fontWeight: 600,
      color: '#334155',
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const tdStyle = {
    padding: '0.75rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    color: '#475569',
  };

  const infoTextStyle = getResponsiveStyle(
    {
      color: '#64748b',
      marginTop: '1rem',
      lineHeight: 1.6,
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.2s',
  };

  const fullWidthCardStyle = {
    ...calculatorCardStyle,
    gridColumn: screenSize === 'mobile' ? 'auto' : '1 / -1',
  };

  const websiteFormGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const hostingFormGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      alignItems: 'center',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }
  );

  const converterIconStyle = {
    textAlign: 'center' as const,
    fontSize: '1.5rem',
    color: colors.secondary,
    fontWeight: 700,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>
        </div>

        <div style={calculatorGridStyle}>
          <div
            style={calculatorCardStyle}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            <h3 style={h3Style}>📊 {t("converterTitle")}</h3>
            <form onSubmit={handleConverterCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("converterAmount")}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    value={converterInputs.amount}
                    onChange={(e) => setConverterInputs({...converterInputs, amount: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                  <select
                    value={converterInputs.unit}
                    onChange={(e) => setConverterInputs({...converterInputs, unit: e.target.value})}
                    style={selectStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="b">{t("unitBits")}</option>
                    <option value="kb">{t("unitKilobits")}</option>
                    <option value="mb">{t("unitMegabits")}</option>
                    <option value="gb">{t("unitGigabits")}</option>
                    <option value="tb">{t("unitTerabits")}</option>
                    <option value="B">{t("unitBytes")}</option>
                    <option value="KB">{t("unitKilobytes")}</option>
                    <option value="MB">{t("unitMegabytes")}</option>
                    <option value="GB">{t("unitGigabytes")}</option>
                    <option value="TB">{t("unitTerabytes")}</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t("convertButton")}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={() => {
                  setConverterInputs({ amount: 500, unit: 'MB' });
                  setConverterResults(null);
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t("clearButton")}
              </button>
            </form>
            {converterResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t("conversionsTitle")}</h3>
                <div style={conversionTableStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("tableUnit")}</th>
                        <th style={thStyle}>{t("tableValue")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {converterResults.map((conv: any, index: number) => (
                        <tr key={index}>
                          <td style={{...tdStyle, borderBottom: index === converterResults.length - 1 ? 'none' : '1px solid #e2e8f0'}}>{conv.label}</td>
                          <td style={{...tdStyle, borderBottom: index === converterResults.length - 1 ? 'none' : '1px solid #e2e8f0'}}>{formatNumber(conv.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div
            style={calculatorCardStyle}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            <h3 style={h3Style}>⏱️ {t("timeCalculatorTitle")}</h3>
            <form onSubmit={handleTimeCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("fileSize")}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    value={timeInputs.fileSize}
                    onChange={(e) => setTimeInputs({...timeInputs, fileSize: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                  <select
                    value={timeInputs.fileSizeUnit}
                    onChange={(e) => setTimeInputs({...timeInputs, fileSizeUnit: e.target.value})}
                    style={selectStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="B">{t("unitBytes")}</option>
                    <option value="KB">{t("unitKilobytes")}</option>
                    <option value="MB">{t("unitMegabytes")}</option>
                    <option value="GB">{t("unitGigabytes")}</option>
                    <option value="TB">{t("unitTerabytes")}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("bandwidth")}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="any"
                    value={timeInputs.bandwidth}
                    onChange={(e) => setTimeInputs({...timeInputs, bandwidth: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                  <select
                    value={timeInputs.bandwidthUnit}
                    onChange={(e) => setTimeInputs({...timeInputs, bandwidthUnit: e.target.value})}
                    style={selectStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="b">{t("unitBitPerSec")}</option>
                    <option value="kb">{t("unitKbitPerSec")}</option>
                    <option value="mb">{t("unitMbitPerSec")}</option>
                    <option value="gb">{t("unitGbitPerSec")}</option>
                    <option value="tb">{t("unitTbitPerSec")}</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t("calculateTimeButton")}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={() => {
                  setTimeInputs({ fileSize: 500, fileSizeUnit: 'MB', bandwidth: 5, bandwidthUnit: 'mb' });
                  setTimeResults(null);
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t("clearButton")}
              </button>
            </form>
            {timeResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t("transferTimeTitle")}</h3>
                <div style={resultGridStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("timeRequired")}</span>
                    <span style={resultValueStyle}>{timeResults.time}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            style={fullWidthCardStyle}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            <h3 style={h3Style}>🌍 {t("websiteCalculatorTitle")}</h3>
            <form onSubmit={handleWebsiteCalculate}>
              <div style={websiteFormGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("pageViews")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="any"
                      value={websiteInputs.pageViews}
                      onChange={(e) => setWebsiteInputs({...websiteInputs, pageViews: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <select
                      value={websiteInputs.pageViewsUnit}
                      onChange={(e) => setWebsiteInputs({...websiteInputs, pageViewsUnit: e.target.value})}
                      style={selectStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="second">{t("perSecond")}</option>
                      <option value="minute">{t("perMinute")}</option>
                      <option value="hour">{t("perHour")}</option>
                      <option value="day">{t("perDay")}</option>
                      <option value="week">{t("perWeek")}</option>
                      <option value="month">{t("perMonth")}</option>
                      <option value="year">{t("perYear")}</option>
                    </select>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("averagePageSize")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="any"
                      value={websiteInputs.pageSize}
                      onChange={(e) => setWebsiteInputs({...websiteInputs, pageSize: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <select
                      value={websiteInputs.pageSizeUnit}
                      onChange={(e) => setWebsiteInputs({...websiteInputs, pageSizeUnit: e.target.value})}
                      style={selectStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="B">{t("unitBytes")}</option>
                      <option value="KB">{t("unitKilobytes")}</option>
                      <option value="MB">{t("unitMegabytes")}</option>
                      <option value="GB">{t("unitGigabytes")}</option>
                      <option value="TB">{t("unitTerabytes")}</option>
                    </select>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("redundancyFactor")}</label>
                  <input
                    type="number"
                    step="any"
                    value={websiteInputs.redundancy}
                    onChange={(e) => setWebsiteInputs({...websiteInputs, redundancy: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t("calculateBandwidthButton")}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={() => {
                  setWebsiteInputs({ pageViews: 5000, pageViewsUnit: 'day', pageSize: 500, pageSizeUnit: 'KB', redundancy: 2 });
                  setWebsiteResults(null);
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t("clearButton")}
              </button>
            </form>
            {websiteResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t("websiteBandwidthTitle")}</h3>
                <div style={resultGridStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("requiredBandwidth")}</span>
                    <span style={resultValueStyle}>{websiteResults.bandwidth}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("monthlyDataUsage")}</span>
                    <span style={resultValueStyle}>{websiteResults.monthly}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            style={fullWidthCardStyle}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            <h3 style={h3Style}>🔄 {t("hostingConverterTitle")}</h3>
            <p style={infoTextStyle}>{t("hostingConverterInfo")}</p>
            <form onSubmit={handleHostingCalculate}>
              <div style={hostingFormGridStyle}>
                <div style={{...formGroupStyle, marginBottom: 0}}>
                  <label style={labelStyle}>{t("monthlyUsage")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="any"
                      value={hostingInputs.monthlyUsage}
                      onChange={(e) => setHostingInputs({...hostingInputs, monthlyUsage: e.target.value})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <select
                      value={hostingInputs.monthlyUsageUnit}
                      onChange={(e) => setHostingInputs({...hostingInputs, monthlyUsageUnit: e.target.value})}
                      style={selectStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="B">{t("unitBytes")}</option>
                      <option value="KB">{t("unitKilobytes")}</option>
                      <option value="MB">{t("unitMegabytes")}</option>
                      <option value="GB">{t("unitGigabytes")}</option>
                      <option value="TB">{t("unitTerabytes")}</option>
                    </select>
                  </div>
                </div>
                <div style={converterIconStyle}>⇄</div>
                <div style={{...formGroupStyle, marginBottom: 0}}>
                  <label style={labelStyle}>{t("bandwidth")}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      step="any"
                      value={hostingInputs.hostingBandwidth}
                      onChange={(e) => setHostingInputs({...hostingInputs, hostingBandwidth: e.target.value})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <select
                      value={hostingInputs.hostingBandwidthUnit}
                      onChange={(e) => setHostingInputs({...hostingInputs, hostingBandwidthUnit: e.target.value})}
                      style={selectStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = colors.primary;
                        e.target.style.boxShadow = `0 0 0 3px rgba(37, 99, 235, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="b">{t("unitBitPerSec")}</option>
                      <option value="kb">{t("unitKbitPerSec")}</option>
                      <option value="mb">{t("unitMbitPerSec")}</option>
                      <option value="gb">{t("unitGbitPerSec")}</option>
                      <option value="tb">{t("unitTbitPerSec")}</option>
                    </select>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t("convertButton")}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={() => {
                  setHostingInputs({ monthlyUsage: '', monthlyUsageUnit: 'GB', hostingBandwidth: '', hostingBandwidthUnit: 'mb' });
                  setHostingResults(null);
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t("clearButton")}
              </button>
            </form>
            {hostingResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t("conversionResultTitle")}</h3>
                <div style={resultGridStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{hostingResults.label}</span>
                    <span style={resultValueStyle}>{hostingResults.value}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightPoint1")}</li>
          <li style={listItemStyle}>{t("insightPoint2")}</li>
          <li style={listItemStyle}>{t("insightPoint3")}</li>
          <li style={listItemStyle}>{t("insightPoint4")}</li>
          <li style={listItemStyle}>{t("insightPoint5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{...h3Style, color: colors.textPrimary}}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={{...h3Style, color: colors.textPrimary}}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={{...h3Style, color: colors.textPrimary}}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={{...h3Style, color: colors.textPrimary}}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion1")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion2")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion3")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion4")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faqQuestion5")}</h3>
          <p style={faqAnswerStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://www.keysight.com/used/us/en/knowledge/calculators/bandwidth-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.calculator.net/bandwidth-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.techtarget.com/searchnetworking/definition/bandwidth"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.geeksforgeeks.org/computer-science-fundamentals/what-is-bandwidth-definition-working-importance-uses/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.ir.com/guides/bandwidth-management"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference5")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}